import { useEffect, useRef, useState } from "react";

/**
 * Reports whether an element's text is *actually* clipped by `truncate` /
 * `line-clamp-*`. Attach the returned `ref` to the clamped element.
 *
 * Re-measures on element resize, on content change, and once webfonts settle.
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>({
    enabled = true,
    lines = 1,
}: {
    enabled?: boolean;
    lines?: number;
} = {}) {
    const ref = useRef<T | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        if (!enabled) return;

        const el = ref.current;
        if (!el) return;

        // 1px tolerance — subpixel rounding otherwise reads as overflow.
        const measure = () => {
            setIsTruncated(
                lines > 1
                    ? el.scrollHeight - el.clientHeight > 1
                    : el.scrollWidth - el.clientWidth > 1,
            );
        };

        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(el);

        // Content can change without the box resizing (fixed-width cells).
        const mutationObserver = new MutationObserver(measure);
        mutationObserver.observe(el, { childList: true, subtree: true, characterData: true });

        let cancelled = false;
        if (typeof document !== "undefined" && document.fonts) {
            void document.fonts.ready.then(() => {
                if (!cancelled) measure();
            });
        }

        return () => {
            cancelled = true;
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [enabled, lines]);

    // Derived rather than reset in the effect, so disabling never leaks a stale reading.
    return { ref, isTruncated: enabled && isTruncated };
}
